import { useState, useMemo } from "react";
import approveBtn from "../../assets/images/yes.png";
import declineBtn from "../../assets/images/No.png";
import noApproveBtn from "../../assets/images/YesBW.png";
import noDeclineBtn from "../../assets/images/NoBW.png";
import { BsThreeDots } from "react-icons/bs";
import { AiTwotoneEdit, AiOutlineEye } from "react-icons/ai";
import EmployeeLeaveMoreDetails from "./EmployeeLeaveMoreDetails";
import ApproveDeclineLeave from "./ApproveDeclineLeave";
import UpdateLeaveStatus from "./UpdateLeaveStatus";
import formatDate from "../../components/Reusable/dateUtils";
import { capitalizeWords } from "../../components/Reusable/capitalizeWords";
import { useLeaveManagementStore } from "../../store/useLeaveManagementStore";
import { subDays } from "date-fns";
import { BiSearch } from "react-icons/bi";
import avatar from "../../assets/images/avatar.webp";

import * as XLSX from "xlsx";
import {
  getStatusColor,
  getStatusText,
  isLeaveProcessed,
  truncateemployeeName,
} from "./leaveManagementFunctions";

const PendingLeaveRequestsTable = ({ selectedCompany }) => {
  const userData = JSON.parse(localStorage.getItem("userData"));
  const loggedEmployeeId = userData?.user?.[0]?.employee_id;
  const [activeMenuId, setActiveMenuId] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [showEmployeeLeaveDetails, setShowEmployeeLeaveDetails] =
    useState(false);
  const [selectedEmployeeData, setSelectedEmployeeData] = useState(null);
  const [showUpdateLeaveDetails, setShowUpdateLeaveDetails] = useState(null);
  const [showApproveDeclinePage, setShowApproveDeclinePage] = useState(null);
  const [sortOrder, setSortOrder] = useState({});

  // Pagination and Search states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");

  const { attendanceData } = useLeaveManagementStore();

  // Filter, search, and sort logic - ALL IN ONE PLACE
  const filteredData = useMemo(() => {
    let filtered =
      attendanceData?.filter(
        (leave) =>
          leave.is_approved === null &&
          leave.is_hr_approved === null &&
          new Date(leave.apply_date) >= subDays(new Date(), 180),
      ) || [];

    // 🔍 Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((employee) => {
        const fullName =
          employee?.user?.employee_full_name?.toLowerCase() || "";
        const employeeId = employee?.user?.employee_id?.toLowerCase() || "";
        const leave_type = employee?.leave_type?.toLowerCase() || "";
        const roleName =
          employee?.user?.roles[0]?.role_name?.toLowerCase() || "";
        const status =
          getStatusText(employee, loggedEmployeeId)?.toLowerCase() || "";

        return (
          fullName.includes(query) ||
          employeeId.includes(query) ||
          leave_type.includes(query) ||
          roleName.includes(query) ||
          status.includes(query)
        );
      });
    }

    // 🔥 Apply sorting
    const sortColumn = Object.keys(sortOrder)[0];
    if (sortColumn) {
      const order = sortOrder[sortColumn];

      filtered.sort((a, b) => {
        if (sortColumn === "status") {
          const statusOrder = { Approved: 1, Declined: 2, Pending: 3 };
          const statusA = getStatusText(a, loggedEmployeeId);
          const statusB = getStatusText(b, loggedEmployeeId);

          return order === "asc"
            ? statusOrder[statusA] - statusOrder[statusB]
            : statusOrder[statusB] - statusOrder[statusA];
        } else if (sortColumn === "employee_full_name") {
          const nameA = a?.user?.employee_full_name || "";
          const nameB = b?.user?.employee_full_name || "";

          return order === "asc"
            ? nameA.localeCompare(nameB)
            : nameB.localeCompare(nameA);
        } else if (sortColumn === "leave_type") {
          const typeA = a.leave_type || "";
          const typeB = b.leave_type || "";

          return order === "asc"
            ? typeA.localeCompare(typeB)
            : typeB.localeCompare(typeA);
        } else if (sortColumn === "apply_date" || sortColumn === "leave_from") {
          const dateA = new Date(a[sortColumn]);
          const dateB = new Date(b[sortColumn]);

          return order === "asc" ? dateA - dateB : dateB - dateA;
        }
        return 0;
      });
    } else {
      // Default sort: newest first by leave_from date
      filtered.sort((a, b) => {
        const dateA = new Date(a.leave_from);
        const dateB = new Date(b.leave_from);
        return dateB - dateA;
      });
    }

    return filtered;
  }, [attendanceData, searchQuery, sortOrder, loggedEmployeeId]);

  // Pagination logic
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = filteredData.slice(startIndex, endIndex);

  // Reset to first page when search query changes
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleLeaveMoreDetails = (leaveId) => {
    const selectedEmployee = attendanceData.find(
      (employee) => employee.leave_id === leaveId,
    );
    if (selectedEmployee) {
      setSelectedEmployeeData(selectedEmployee);
      setShowEmployeeLeaveDetails(true);
      setActiveMenuId(false);
    }
  };

  const handleUpdateLeaveDetails = (leaveId) => {
    const selectedEmployee = attendanceData.find(
      (employee) => employee.leave_id === leaveId,
    );
    if (selectedEmployee) {
      setSelectedEmployeeData(selectedEmployee);
      setShowUpdateLeaveDetails(true);
      setActiveMenuId(false);
    }
  };

  const handleLeaveApprove = (leaveId) => {
    const selectedEmployee = attendanceData.find(
      (employee) => employee.leave_id === leaveId,
    );
    if (selectedEmployee) {
      setSelectedEmployeeData(selectedEmployee);
      setShowApproveDeclinePage(true);
    }
  };

  const handleLeaveDecline = (leaveId) => {
    const selectedEmployee = attendanceData.find(
      (employee) => employee.leave_id === leaveId,
    );
    if (selectedEmployee) {
      setSelectedEmployeeData(selectedEmployee);
      setShowApproveDeclinePage(true);
    }
  };

  const handleToggleMenu = (leaveId, e) => {
    if (activeMenuId === leaveId) {
      setActiveMenuId(null);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      const screenWidth = window.innerWidth;

      setActiveMenuId(leaveId);
      const menuWidth = 100;
      const menuLeft = rect.left + window.scrollX;
      const menuRight = menuLeft + menuWidth;

      if (menuRight > screenWidth) {
        setMenuPosition({
          top: rect.bottom + window.scrollY - 15,
          right: screenWidth - menuLeft - 20,
        });
      } else {
        setMenuPosition({
          top: rect.bottom + window.scrollY - 15,
          left: menuLeft - 20,
        });
      }
    }
  };

  const handleSort = (columnName) => {
    const currentOrder = sortOrder[columnName];
    const newOrder = currentOrder === "asc" ? "desc" : "asc";
    setSortOrder({ [columnName]: newOrder });
    setCurrentPage(1);
  };

  const goToPreviousPage = () => {
    setCurrentPage((prevPage) => prevPage - 1);
  };

  const goToNextPage = () => {
    setCurrentPage((prevPage) => prevPage + 1);
  };

  const handleDownloadExcel = () => {
    // console.log("🔥 firing", filteredData.length);

    if (!filteredData || filteredData.length === 0) return;

    const rows = filteredData.map((leave) => {
      const pendingManagers =
        leave?.approving_managers_status
          ?.filter((manager) => manager.status?.toLowerCase() === "pending")
          .map((manager) => manager.manager_name)
          .join(", ") || "";

      return {
        "Employee ID": leave?.user?.employee_id || "",
        "Employee Name": leave?.user?.employee_full_name || "",
        Role: leave?.user?.roles?.[0]?.role_name || "",
        "Applied Date": formatDate(leave.apply_date),
        "Leave From": formatDate(leave.leave_from),
        "Leave To": formatDate(leave.leave_to),
        Days: leave.days_count,
        "Leave Type": leave.leave_type || "",
        Status: getStatusText(leave, loggedEmployeeId) || "",
        "Pending Managers": pendingManagers,
      };
    });

    if (rows.length === 0) return;

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Leave Requests");

    const colWidths = Object.keys(rows[0]).map((key) => ({
      wch: Math.max(
        key.length,
        ...rows.map((r) => String(r[key] ?? "").length),
      ),
    }));
    worksheet["!cols"] = colWidths;

    XLSX.writeFile(workbook, "Leave_Requests.xlsx");
  };

  return (
    <div>
      {/* Search Bar */}

      <div className="p-4 flex justify-between items-center gap-4 flex-wrap">
        <div className="relative flex-1 min-w-[250px] max-w-xs">
          <input
            type="text"
            placeholder="Search "
            value={searchQuery}
            onChange={handleSearchChange}
            className="w-full px-4 py-2.5 rounded-full border border-gray-300 text-sm focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-all"
          />
          <BiSearch className="absolute top-3 right-4 text-2xl text-gray-400" />
        </div>
        {searchQuery && (
          <button
            onClick={() => setSearchQuery("")}
            className="px-5 py-2.5 rounded-lg text-green-500 bg-white border-1 border-green-500 text-sm font-medium hover:bg-green-500 hover:text-white transition-colors"
          >
            Clear Search
          </button>
        )}
        <button
          onClick={handleDownloadExcel}
          className="px-5 py-2.5 rounded-lg text-white bg-green-500 border border-green-500 text-sm font-medium hover:bg-green-600 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <i className="fa-solid fa-file-excel" />
          Download Excel
        </button>
      </div>

      <table className="leave_table">
        <thead>
          <tr>
            <th
              colSpan={2}
              onClick={() => handleSort("employee_full_name")}
              style={{ cursor: "pointer" }}
            >
              Employee Details{" "}
              {sortOrder["employee_full_name"] === "asc"
                ? "↑"
                : sortOrder["employee_full_name"] === "desc"
                  ? "↓"
                  : ""}
            </th>
            <th
              onClick={() => handleSort("apply_date")}
              style={{ cursor: "pointer" }}
            >
              Applied Date{" "}
              {sortOrder["apply_date"] === "asc"
                ? "↑"
                : sortOrder["apply_date"] === "desc"
                  ? "↓"
                  : ""}
            </th>
            <th
              onClick={() => handleSort("leave_from")}
              style={{ cursor: "pointer" }}
            >
              Start Date{" "}
              {sortOrder["leave_from"] === "asc"
                ? "↑"
                : sortOrder["leave_from"] === "desc"
                  ? "↓"
                  : ""}
            </th>
            <th>End Date</th>
            <th>Days</th>
            <th
              onClick={() => handleSort("leave_type")}
              style={{ cursor: "pointer" }}
            >
              Type{" "}
              {sortOrder["leave_type"] === "asc"
                ? "↑"
                : sortOrder["leave_type"] === "desc"
                  ? "↓"
                  : ""}
            </th>
            <th
              onClick={() => handleSort("status")}
              style={{ cursor: "pointer" }}
            >
              Status{" "}
              {sortOrder["status"] === "asc"
                ? "↑"
                : sortOrder["status"] === "desc"
                  ? "↓"
                  : ""}
            </th>
            <th>Actions</th>
            <th>More</th>
          </tr>
        </thead>
        <tbody>
          {currentData.length === 0 ? (
            <tr>
              <td
                colSpan={10}
                className="text-center py-8 text-sm text-gray-400"
              >
                {searchQuery
                  ? "No results found for your search."
                  : "No pending leave requests."}
              </td>
            </tr>
          ) : (
            currentData.map((employee, index) => (
              <tr key={index} id={employee.leave_id}>
                <td className="leave_empImage">
                  <img
                    src={employee?.user?.profile_picture || avatar}
                    alt="Employee"
                  />
                </td>
                <td className="leave_empDetails">
                  <div className="tooltip-container">
                    <span className="tooltip-text">
                      {employee?.user?.employee_full_name}
                    </span>
                  </div>
                  <span>
                    {capitalizeWords(
                      truncateemployeeName(employee?.user?.employee_full_name),
                    )}
                  </span>

                  <div className="leave_designation">
                    <span style={{ fontSize: "10px" }}>
                      {employee?.user?.roles[0]?.role_name}
                    </span>
                  </div>
                  <div className="leave_id">
                    <span style={{ fontSize: "10px" }}>
                      ID:{employee?.user?.employee_id}
                    </span>
                  </div>
                </td>
                <td style={{ fontSize: "12px" }}>
                  {formatDate(employee.apply_date)}
                </td>
                <td style={{ fontSize: "12px" }}>
                  {formatDate(employee.leave_from)}
                </td>
                <td style={{ fontSize: "12px" }}>
                  {formatDate(employee.leave_to)}
                </td>
                <td style={{ fontSize: "12px" }}>
                  {employee.days_count}{" "}
                  {employee.days_count > 1 ? "Days" : "Day"}
                </td>
                <td style={{ fontSize: "12px" }}>{employee.leave_type}</td>
                <td
                  style={{ color: getStatusColor(employee, loggedEmployeeId) }}
                >
                  {getStatusText(employee, loggedEmployeeId)}
                </td>
                <td style={{ fontSize: "12px" }}>
                  {isLeaveProcessed(employee, loggedEmployeeId) ? (
                    <>
                      <button
                        className="btn btn-approve"
                        onClick={() => handleLeaveApprove(employee.leave_id)}
                      >
                        <img src={noApproveBtn} alt="approve" />
                      </button>
                      <button
                        type="button"
                        className="btn btn-decline"
                        onClick={() => handleLeaveDecline(employee.leave_id)}
                      >
                        <img src={noDeclineBtn} alt="decline" />
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        className="btn btn-approve"
                        onClick={() => handleLeaveApprove(employee.leave_id)}
                      >
                        <img src={approveBtn} alt="approve" />
                      </button>
                      <button
                        type="button"
                        className="btn btn-decline"
                        onClick={() => handleLeaveDecline(employee.leave_id)}
                      >
                        <img src={declineBtn} alt="decline" />
                      </button>
                    </>
                  )}
                </td>
                <td style={{ fontSize: "12px" }}>
                  <span
                    className="leave_icon-dots"
                    onClick={(e) => handleToggleMenu(employee.leave_id, e)}
                  >
                    <BsThreeDots />
                  </span>
                  {activeMenuId === employee.leave_id && (
                    <div
                      className="absolute right-6 top-[calc(100%+12px)] w-fit bg-white rounded-xl shadow-lg p-3 border border-gray-200 transform transition-all duration-300 ease-in-out scale-100 opacity-100"
                      style={menuPosition}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ul className="flex flex-col list-none p-0 m-0 space-y-1">
                        <li
                          className="flex items-center gap-3 text-gray-700 font-medium cursor-pointer px-4 py-2 rounded-lg hover:bg-gradient-to-r hover:from-green-500 hover:to-green-400 hover:text-white transition-all duration-300 ease-in-out"
                          onClick={() =>
                            handleLeaveMoreDetails(employee.leave_id)
                          }
                        >
                          <span className="flex items-center gap-2 text-sm">
                            <AiOutlineEye className="text-base" />
                            View Details
                          </span>
                        </li>
                        <li
                          className="flex items-center gap-3 text-gray-700 font-medium cursor-pointer px-4 py-2 rounded-lg hover:bg-gradient-to-r hover:from-green-500 hover:to-green-400 hover:text-white transition-all duration-300 ease-in-out"
                          onClick={() =>
                            handleUpdateLeaveDetails(employee.leave_id)
                          }
                        >
                          <span className="flex items-center gap-2 text-sm">
                            <AiTwotoneEdit className="text-base" />
                            Edit Leave
                          </span>
                        </li>
                      </ul>
                    </div>
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {searchQuery ? null : (
        <div className="pagination">
          <div>
            <button onClick={goToPreviousPage} disabled={currentPage === 1}>
              <i className="fa-solid fa-arrow-left-long"></i> Previous
            </button>
          </div>
          <div>
            {Array.from({ length: totalPages }, (_, index) => {
              const startPage = currentPage - 5 > 0 ? currentPage - 5 : 1;
              const endPage =
                startPage + 9 < totalPages ? startPage + 9 : totalPages;
              return (
                index + 1 >= startPage &&
                index + 1 <= endPage && (
                  <button
                    key={index}
                    onClick={() => handlePageChange(index + 1)}
                    className={currentPage === index + 1 ? "active" : ""}
                  >
                    {index + 1}
                  </button>
                )
              );
            })}
          </div>
          <div>
            <button onClick={goToNextPage} disabled={currentPage >= totalPages}>
              Next <i className="fa-solid fa-arrow-right-long"></i>
            </button>
          </div>
        </div>
      )}

      {showEmployeeLeaveDetails && (
        <div className="popup-overlay">
          <div className="popup-content">
            <EmployeeLeaveMoreDetails
              employeeData={selectedEmployeeData}
              setShowEmployeeLeaveDetails={setShowEmployeeLeaveDetails}
              selectedCompany={selectedCompany}
            />
          </div>
        </div>
      )}
      {showUpdateLeaveDetails && (
        <div className="popup-overlay">
          <div className="popup-content">
            <UpdateLeaveStatus
              employeeData={selectedEmployeeData}
              setShowUpdateLeaveDetails={setShowUpdateLeaveDetails}
              setSelectedEmployeeData={setSelectedEmployeeData}
            />
          </div>
        </div>
      )}
      {showApproveDeclinePage && (
        <div className="popup-overlay">
          <div className="popup-content">
            <ApproveDeclineLeave
              employeeData={selectedEmployeeData}
              setShowApproveDeclinePage={setShowApproveDeclinePage}
              selectedCompany={selectedCompany}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingLeaveRequestsTable;
